/* Supervisors — who covers what.
   No mockup for this screen; `docs/design/merchandisers.html` is the visual reference for a
   people list, and the shape is spec/03 archetype A: page head → filter card → segments →
   table → sticky bulk bar.

   Built out of what already exists rather than new parts: `EntitySelect` for the region,
   `FilterChips` + `useLabelCache` for the applied-filter row, `getAbortable` for the request,
   and the same table/pagination/bulk-bar composition as `/visit`. The only new code here is the
   columns, the two derived filters, and the activate/deactivate call.

   The list's job is to answer an operational question, not to mirror the API (spec/03): a
   supervisor responsible for **no points** covers nothing, so that is a first-class filter,
   next to the active/inactive split.

   Everything narrows on the SERVER. `GET supervisors/` gained `county`, `is_active`, `no_points`
   and a `points_count` annotation in the same change (picsell-api
   `users/api/web/v1/filters.py`) — before it, the endpoint could only be searched and sorted, so
   a region filter here would have been decorative.

   ⚠ Route access is `supervisor_manager` only (`AppNav.MENU`), which is what makes deactivating
   a peer a manager action rather than something any supervisor can do to another. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Col, Empty, Flex, Form, Input, Modal, Row, Segmented, Space, Table, Tag, Tooltip, Typography } from "antd";
import { CheckCircleOutlined, CompassOutlined, ExportOutlined, MinusCircleOutlined, PushpinOutlined, SearchOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { PersonCell, RowActions, StatusIcon, TableFoot, actionsColumn, dash, statusColumn, useTableScrollY } from "../../component/table-cells";
import {
  DOCUMENTS,
  ENTITY_DROPDOWN_SETTINGS,
  PHOTO_REPORT,
  POINTS_ADMIN,
  SIDE_NAV,
  SUPERVISOR_LIST,
  TRANSLATION_DOWNLOADER,
  VISIT_PAGE,
} from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import { fill, regionId, translate } from "../../util/util";

import "./supervisors.scss";

const PAGE_SIZES = [25, 50, 100];

const SEGMENTS = [
  ["all", "seg_all"],
  ["active", "seg_active"],
  ["inactive", "seg_inactive"],
];

/* Column key → the field the API orders on. A key missing here is not sortable. */
const ORDERING = { name: "first_name", points: "points_count" };

const FIELD_COL = { xs: { flex: "0 0 100%" }, md: { flex: "0 0 50%" }, xl: { flex: "0 0 33.3333%" } };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/** The request the table runs on — one place where the endpoint's parameter shape is visible. */
function buildQuery({ search, county, segment, noPoints, page, size, sort }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (search) p.set("search", search);
  if (county) p.set("county", String(idOf(county)));
  if (segment !== "all") p.set("is_active", segment === "active" ? "true" : "false");
  if (noPoints) p.set("no_points", "true");
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

/** URL ↔ state, so a filtered view is shareable and survives Back from a supervisor's page. */
function stateToSearch({ search, county, segment, noPoints, page, size, sort }) {
  const p = new URLSearchParams();
  if (search) p.set("q", search);
  if (county) p.set("county", String(idOf(county)));
  if (segment !== "all") p.set("segment", segment);
  if (noPoints) p.set("no_points", "1");
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString();
}

function searchToState(locationSearch, me) {
  const p = new URLSearchParams(locationSearch);
  const sort = p.get("sort");
  const hasFilters = Boolean(locationSearch && locationSearch.replace(/^\?/, ""));
  return {
    search: p.get("q") || "",
    /* Without a URL to restore, the manager's own region is the sensible default scope — the
       same seeding the visits list does. */
    county: p.get("county") || (hasFilters ? "" : regionId(me) || ""),
    segment: SEGMENTS.some(([value]) => value === p.get("segment")) ? p.get("segment") : "all",
    noPoints: p.get("no_points") === "1",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(SUPERVISOR_LIST, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  /* Read once. After mount this component owns the state and writes the URL, not the reverse. */
  const initial = useRef(searchToState(location.search, me)).current;

  const [search, setSearch] = useState(initial.search);
  const [county, setCounty] = useState(initial.county);
  const [segment, setSegment] = useState(initial.segment);
  const [noPoints, setNoPoints] = useState(initial.noPoints);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  /* The option object the region select handed back, so a chosen value labels itself. */
  const [chosenCounty, setChosenCounty] = useState(null);
  /* Typing is separate from the applied term: the field must not lag the keystroke while the
     request that the keystroke will eventually cause is still debounced. */
  const [typed, setTyped] = useState(initial.search);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  /* --------------------------------------------------------------- fetch */

  const query = useMemo(() => buildQuery({ search, county, segment, noPoints, page, size, sort }), [search, county, segment, noPoints, page, size, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`supervisors/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  /* 250ms, matching every other search box in the app: one request per pause, not per keystroke. */
  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
      setSelected([]);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  /* The URL trails the state; `replace` so filtering does not fill the history stack. */
  useEffect(() => {
    const next = stateToSearch({ search, county, segment, noPoints, page, size, sort });
    if (next !== location.search.replace(/^\?/, "")) history.replace({ search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, county, segment, noPoints, page, size, sort]);

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which renders an empty table over a non-empty result. */
  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const resetFilters = narrow(() => {
    setTyped("");
    setSearch("");
    setCounty("");
    setChosenCounty(null);
    setSegment("all");
    setNoPoints(false);
    setSort(null);
  });

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache();

  const chips = useMemo(() => {
    const out = [];
    if (search) out.push({ key: "q", label: translate(DOCUMENTS, "search", lang), value: search });
    if (county) out.push({ key: "county", label: td("region"), value: nameFor("county", chosenCounty ?? county) });
    if (noPoints) out.push({ key: "no_points", label: t("no_points"), value: t("no_points_hint") });
    return out;
    /* `labels` is a dependency: a chip that arrived as a bare id has to re-render once its name
       lands from `EntitySelect`'s resolution. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, county, chosenCounty, noPoints, labels, lang]);

  const clearChip = narrow(key => {
    if (key === "q") {
      setTyped("");
      setSearch("");
    } else if (key === "county") {
      setCounty("");
      setChosenCounty(null);
    } else if (key === "no_points") {
      setNoPoints(false);
    }
  });

  /* ------------------------------------------------------ activate / deactivate */

  const patchActive = (row, value) =>
    new Promise(resolve => {
      call({ method: "PATCH", url: `supervisors/${row.id}/`, post: { is_active: value } }, response => resolve(Boolean(response)));
    });

  /* Rows are patched in place rather than refetched, so the table does not jump under the
     cursor. A row that no longer matches the active segment stays until the next fetch: its
     status has already changed, so it is showing the result of the action, not a stale state. */
  const applyLocally = (ids, value) => {
    setRows(prev => prev.map(row => (ids.includes(row.id) ? { ...row, is_active: value } : row)));
  };

  const runChange = async (targets, value) => {
    if (!targets.length) {
      setSelected([]);
      return;
    }
    setBusy(true);
    const results = await Promise.all(targets.map(row => patchActive(row, value)));
    setBusy(false);
    const done = targets.filter((row, i) => results[i]);
    if (done.length) {
      applyLocally(
        done.map(row => row.id),
        value
      );
      Alert.success(fill(t(value ? "activated_msg" : "deactivated_msg"), { n: done.length }), { position: "top-right" });
    }
    if (done.length < targets.length) {
      Alert.warning(fill(t("change_failed_msg"), { n: targets.length - done.length }), { position: "top-right" });
    }
    setSelected([]);
  };

  /* Deactivation is destructive, so it is confirmed and the confirm says what and how many
     (spec/03). Activation is not, so it just runs. */
  const changeSelected = value => {
    const targets = rows.filter(row => selected.includes(row.id) && row.is_active !== value);
    if (value) {
      runChange(targets, true);
      return;
    }
    Modal.confirm({
      title: fill(t("confirm_deactivate"), { n: targets.length }),
      okText: t("deactivate"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () => runChange(targets, false),
    });
  };

  const openSupervisor = row => history.push(`/supervisors/${row.id}`);

  /* ------------------------------------------------------------- columns */

  const columns = useMemo(
    () => [
      statusColumn((value, row) => (
        <StatusIcon
          Icon={row.is_active ? CheckCircleOutlined : MinusCircleOutlined}
          tone={row.is_active ? "ok" : "off"}
          label={t(row.is_active ? "status_active" : "status_inactive")}
        />
      )),
      {
        key: "name",
        title: td("supervisor"),
        width: 280,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => <PersonCell user={row} />,
      },
      {
        key: "phone",
        title: translate(POINTS_ADMIN, "f_phone", lang),
        dataIndex: "phone_number",
        width: 170,
        render: value => value || dash,
      },
      {
        key: "region",
        title: td("region"),
        width: 200,
        ellipsis: true,
        render: (value, row) => row.county_name || <Tooltip title={t("no_region")}>{dash}</Tooltip>,
      },
      {
        key: "points",
        title: td("points"),
        dataIndex: "points_count",
        width: 130,
        align: "right",
        sorter: true,
        /* Zero is the signal the page is built around, so it is called out in words as well as
           colour — colour is never the only signal (spec/01). */
        render: value =>
          value ? (
            <span className="lp-num">{value}</span>
          ) : (
            <Tooltip title={t("no_points_hint")}>
              <Tag className="sv-tag--warn">{t("no_points")}</Tag>
            </Tooltip>
          ),
      },
      actionsColumn((value, row) => (
        <RowActions
          label={tv("row_actions")}
          items={[
            { key: "open", icon: <ExportOutlined />, label: t("open") },
            row.is_active
              ? { key: "deactivate", icon: <MinusCircleOutlined />, label: t("deactivate"), danger: true }
              : { key: "activate", icon: <CheckCircleOutlined />, label: t("activate") },
          ]}
          onClick={key => {
            if (key === "open") openSupervisor(row);
            else if (key === "activate") runChange([row], true);
            else
              Modal.confirm({
                title: fill(t("confirm_deactivate"), { n: 1 }),
                okText: t("deactivate"),
                okButtonProps: { danger: true },
                /* `Modal.confirm` is a static call, so it renders outside the tree and does not
                   inherit `AppShell`'s `ConfigProvider locale` — without these the buttons stay
                   English whatever the account's language is. */
                cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
                onOk: () => runChange([row], false),
              });
          }}
        />
      )),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, rows]
  );

  const scrollY = useTableScrollY();

  return (
    <Flex vertical gap="middle" className="shell-content lp sv">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "supervisors", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              <Form.Item label={translate(DOCUMENTS, "search", lang)}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t("search_ph")} value={typed} onChange={event => setTyped(event.target.value)} />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("region")}>
                <EntitySelect
                  entity="county"
                  value={county}
                  seed={chosenCounty || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={narrow(option => {
                    setChosenCounty(option);
                    setCounty(option ? idOf(option) : "");
                  })}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented value={segment} onChange={narrow(setSegment)} options={SEGMENTS.map(([value, key]) => ({ value, label: t(key) }))} />
            {/* A derived-state filter: "responsible for nothing" is the question this list is
                asked, and it is not a column the user can eyeball. */}
            <Tag.CheckableTag className="sv-chip" checked={noPoints} onChange={narrow(setNoPoints)}>
              <Space size={6}>
                <PushpinOutlined />
                {t("no_points")}
              </Space>
            </Tag.CheckableTag>
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "supervisors", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 880, y: scrollY }}
          rowClassName={row => (selected.includes(row.id) ? "lp-row lp-row--on" : "lp-row")}
          rowSelection={{ fixed: true, selectedRowKeys: selected, onChange: keys => setSelected(keys) }}
          onRow={row => ({
            onClick: event => {
              /* The selection cell and the row menu own their clicks; everything else opens. */
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openSupervisor(row);
            },
          })}
          /* Paging is the footer below, so the only thing left for the table's own onChange is
             the sorter — with `pagination={false}` its first argument carries nothing. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{t("empty_title")}</Typography.Text>
                    <Typography.Text type="secondary">{t("empty_desc")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="sv"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          <Space size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            <Button size="small" icon={<CheckCircleOutlined />} loading={busy} onClick={() => changeSelected(true)}>
              {t("activate")}
            </Button>
            <Button size="small" danger type="primary" icon={<MinusCircleOutlined />} loading={busy} onClick={() => changeSelected(false)}>
              {t("deactivate")}
            </Button>
          </Space>
        </Flex>
      )}
    </Flex>
  );
};

export default List;
